import { useState } from 'react';
import { ExternalLink, Github, Sparkles, ImageOff, Briefcase } from 'lucide-react';
import zyloa1 from '../Zyloa.png';
import zyloa2 from '../Zyloa2.png';
import zyloa3 from '../Zyloa3.png';
import thrive1 from '../thrive1.png';
import thrive2 from '../thrive2.png';
import thrive3 from '../thrive3.png';
import tech1 from '../12521.png';
import tech2 from '../12522.png';
import tech3 from '../12523.png';
import ramu1 from '../ramu1.png';
import ramu2 from '../ramu2.png';
import ramu3 from '../ramu3.png';
import bluemine1 from '../bluemine1.png';
import bluemine2 from '../bluemine2.png';
import bluemine3 from '../bluemine3.png';

const PROJECTS = [
  {
    title: 'Zyloa',
    tagline: 'Personal Productivity & Finance Dashboard',
    problem:
      "Day-to-day life gets scattered across too many apps — one for tasks, another for budgeting, another for habits, another for mood or journaling.",
    solution:
      "Built an all-in-one personal dashboard that brings tasks, habits, expenses, investments, savings goals, and mood tracking into a single, clean interface — with an AI assistant that helps plan the day and make sense of spending patterns.",
    tech: ['React', 'Node.js', 'Firebase', 'Tailwind CSS', 'AI Integration'],
    link: 'https://zyloa.vercel.app/',
    isLive: true,
    images: [zyloa1, zyloa2, zyloa3],
  },
];

// Freelance / client work — compact cards, up to 3 images each.
// Import each project's screenshots the same way as the Zyloa/THRIVE images above,
// then reference them in that project's `images` array below.
const FREELANCE_PROJECTS = [
  {
    title: 'THRIVE Therapy and Rehab',
    tagline: 'Pediatric speech & autism therapy clinic — Chennai',
    description:
      "Website for a pediatric therapy clinic offering speech, occupational, and developmental support for children with autism, ADHD, cerebral palsy, and related needs — covering services, mission, and appointment booking.",
    tech: ['React', 'Tailwind CSS'],
    link: 'https://www.thrivetherapyandrehab.com/',
    images: [thrive1, thrive2, thrive3],
  },
  {
    title: '1252 Technology',
    tagline: 'Enterprise IT infrastructure & networking — Singapore',
    description:
      "Corporate site for a Singapore-based IT solutions provider specializing in enterprise networking, secure connectivity, hardware supply, and managed services — built around their brand story and reliability-first positioning.",
    tech: ['React', 'Tailwind CSS'],
    link: 'https://www.1252technology.com/',
    images: [tech1, tech2, tech3],
  },
  {
    title: 'Ramu & Co Jewellers',
    tagline: 'Heritage gold & silver jewellery showroom — Trichy',
    description:
      "Website for a jewellery showroom trusted since 1965 — showcasing gold and silver collections, customized jewellery design, awards, and a six-decade legacy timeline for a trademarked Trichy institution.",
    tech: ['React', 'Tailwind CSS'],
    link: 'https://www.ramuandcojewellerstrichy.com/',
    images: [ramu1, ramu2, ramu3],
  },
  {
    title: 'BlueMine Technologies',
    tagline: 'Software solutions & digital marketing agency — India',
    description:
      "Website for a software solutions and digital marketing company — showcasing services across digital marketing, social media management, web development, and data analytics, with a built-in consultation request form.",
    tech: ['React', 'Tailwind CSS'],
    link: 'https://blueminetech.com/',
    images: [bluemine1, bluemine2, bluemine3],
  },
];

function ProjectGallery({ images, title }) {
  const [active, setActive] = useState(0);

  return (
    <div>
      <div className="rounded-xl overflow-hidden border border-slate-200 bg-slate-100 h-64 sm:h-80 flex items-center justify-center">
        <img
          src={images[active]}
          alt={`${title} screenshot ${active + 1}`}
          className="w-full h-full object-contain"
        />
      </div>
      {images.length > 1 && (
        <div className="flex gap-3 mt-3">
          {images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`flex-1 rounded-lg overflow-hidden border-2 transition-colors bg-slate-100 h-16 sm:h-20 flex items-center justify-center ${
                active === i ? 'border-violet-500' : 'border-transparent opacity-60 hover:opacity-100'
              }`}
            >
              <img
                src={img}
                alt={`${title} thumbnail ${i + 1}`}
                className="w-full h-full object-contain"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function FreelanceCard({ project }) {
  const slots = [0, 1, 2];

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col">
      <div className="grid grid-cols-3 gap-1.5 mb-4">
        {slots.map((i) => {
          const img = project.images[i];
          return (
            <div
              key={i}
              className="aspect-[4/3] rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center"
            >
              {img ? (
                <img
                  src={img}
                  alt={`${project.title} screenshot ${i + 1}`}
                  className="w-full h-full object-cover"
                />
              ) : (
                <ImageOff size={16} className="text-slate-300" />
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-1.5 text-xs font-semibold text-pink-600 mb-1.5">
        <Briefcase size={12} />
        CLIENT PROJECT
      </div>
      <h4 className="font-display font-semibold text-lg text-slate-900 mb-1">
        {project.title}
      </h4>
      <p className="text-sm text-slate-500 mb-2.5">{project.tagline}</p>
      <p className="text-sm text-slate-600 leading-relaxed mb-4 flex-1">
        {project.description}
      </p>

      <div className="flex flex-wrap gap-1.5 mb-4">
        {project.tech.map((tech) => (
          <span
            key={tech}
            className="px-2.5 py-1 rounded-full text-xs font-medium bg-pink-50 text-pink-700 border border-pink-100"
          >
            {tech}
          </span>
        ))}
      </div>

      <a
        href={project.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-violet-600 hover:text-violet-700 transition-colors"
      >
        <ExternalLink size={15} />
        Visit Site
      </a>
    </div>
  );
}

export default function Projects() {
  return (
    <section id="projects" className="py-24 px-6 lg:px-8 bg-slate-50/60">
      <div className="max-w-6xl mx-auto">
        <h2 className="font-display font-bold text-4xl sm:text-5xl text-slate-900 mb-4">
          Featured Projects
        </h2>
        <p className="text-slate-500 text-lg max-w-2xl mb-14">
          Things I've built for real use, not just to fill a portfolio.
        </p>

        <div className="space-y-16">
          {PROJECTS.map((project) => (
            <div
              key={project.title}
              className="grid lg:grid-cols-2 gap-10 items-start rounded-3xl border border-slate-200 bg-white p-6 sm:p-8"
            >
              <ProjectGallery images={project.images} title={project.title} />

              <div>
                <div className="flex items-center gap-2 text-xs font-semibold text-violet-600 mb-2">
                  <Sparkles size={14} />
                  PERSONAL PROJECT
                </div>
                <h3 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-1">
                  {project.title}
                </h3>
                <p className="text-slate-500 mb-6">{project.tagline}</p>

                <div className="space-y-4 mb-6">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">
                      The problem
                    </p>
                    <p className="text-slate-600 leading-relaxed">{project.problem}</p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">
                      What I built
                    </p>
                    <p className="text-slate-600 leading-relaxed">{project.solution}</p>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.tech.map((tech) => (
                    <span
                      key={tech}
                      className="px-3 py-1 rounded-full text-sm font-medium bg-violet-50 text-violet-700 border border-violet-100"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <a
                  href={project.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 font-semibold text-violet-600 hover:text-violet-700 transition-colors"
                >
                  {project.isLive ? <ExternalLink size={18} /> : <Github size={18} />}
                  {project.isLive ? 'View Live' : 'View on GitHub'}
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Freelance / client work */}
        {FREELANCE_PROJECTS.length > 0 && (
          <div className="mt-20">
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-slate-900 mb-2">
              Client Work
            </h3>
            <p className="text-slate-500 mb-8">
              Freelance projects delivered for real clients.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {FREELANCE_PROJECTS.map((project) => (
                <FreelanceCard key={project.title} project={project} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}